import React, { useEffect, useRef, useState } from 'react';
import { Text as RNText, TextProps as RNTextProps, TextStyle, StyleSheet, View, Platform } from 'react-native';

import { Icon } from './icon';
import { IconButton } from './icon-button';

import { useTheme } from '@/components/theme-provider';
import { typography, colors as baseColors } from '@/styles';

export interface TextProps extends RNTextProps {
	variant?: 'headlineLarge'
		| 'headlineMedium'
		| 'headlineSmall'
		| 'titleLarge'
		| 'titleMedium'
		| 'titleSmall'
		| 'bodyLarge'
		| 'bodyMedium'
		| 'bodySmall'
		| 'labelLarge'
		| 'labelMedium'
		| 'labelSmall';
	copyable?: boolean;
	copyText?: string;
}

/** Line height iOS needs to fit a font's ascenders: SF's natural line height is ~1.19× its size. */
const NATIVE_LINE_HEIGHT_RATIO = 1.2;

/**
 * A caller that overrides `fontSize` without a `lineHeight` keeps the variant's line height —
 * bodyMedium's 20 under a 28pt hero. Web lets the glyphs overflow a short line box; iOS clips
 * them to the text frame, cutting off the top of every large number and heading. Native only,
 * so web rendering is unchanged.
 */
const nativeLineHeightFor = (variantLineHeight: number, style: TextProps['style']): TextStyle | null => {
	if (Platform.OS === 'web') return null;
	const flat = StyleSheet.flatten(style) as TextStyle | undefined;
	if (flat?.fontSize == null || flat.lineHeight != null) return null;
	const needed = Math.ceil(flat.fontSize * NATIVE_LINE_HEIGHT_RATIO);
	return needed > variantLineHeight ? { lineHeight: needed } : null;
};

export const Text: React.FC<TextProps> = ({
	style,
	variant = 'bodyMedium',
	copyable,
	copyText,
	...props
}) => {
	const theme = useTheme();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<any>(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const variantStyles = {
		headlineLarge: styles.headlineLarge,
		headlineMedium: styles.headlineMedium,
		headlineSmall: styles.headlineSmall,
		titleLarge: styles.titleLarge,
		titleMedium: styles.titleMedium,
		titleSmall: styles.titleSmall,
		bodyLarge: styles.bodyLarge,
		bodyMedium: styles.bodyMedium,
		bodySmall: styles.bodySmall,
		labelLarge: styles.labelLarge,
		labelMedium: styles.labelMedium,
		labelSmall: styles.labelSmall,
	};

	const content = (
		<RNText
			style={[
				variantStyles[variant],
				{
					color: theme.colors.onBackground,
					fontFamily: typography.fontFamily,
				},
				style,
				nativeLineHeightFor(variantStyles[variant].lineHeight, style),
			]}
			{...props}
		/>
	);

	if (copyable && Platform.OS === 'web') {
		const resolvedText = typeof copyText === 'string' ? copyText : typeof props.children === 'string' || typeof props.children === 'number' ? String(props.children) : '';

		const handleCopy = async () => {
			try {
				if (resolvedText) {
					if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
						await navigator.clipboard.writeText(resolvedText);
					} else {
						const ta = document.createElement('textarea');
						ta.value = resolvedText;
						ta.style.position = 'fixed';
						ta.style.opacity = '0';
						document.body.appendChild(ta);
						ta.focus();
						ta.select();
						document.execCommand('copy');
						document.body.removeChild(ta);
					}

					setCopied(true);
					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current);
					}
					timeoutRef.current = setTimeout(() => setCopied(false), 3000) as any;
				}
			} catch {
				setCopied(false);
				console.error('Failed to copy text');
			}
		};

		return (
			<View style={styles.copyContainer}>
				{content}
				{copied ? (
					<View style={styles.copiedContainer}>
						<Icon name='checkmark-circle' size={16} color={baseColors.success} />
						<RNText style={[styles.copiedText, { color: baseColors.success, fontFamily: typography.fontFamily }]}>
							Copied
						</RNText>
					</View>
				) : (
					<IconButton
						icon='clipboard-outline'
						size={16}
						onPress={handleCopy}
						iconColor={theme.colors.onSurfaceVariant}
						accessibilityLabel='Copy text'
						style={styles.copyButton}
					/>
				)}
			</View>
		);
	}

	return content;
};

const styles = StyleSheet.create({
	copyContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	copiedContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		marginLeft: 6,
	},
	copyButton: {
		marginLeft: 6,
	},
	copiedText: {
		marginLeft: 4,
		fontSize: 12,
		fontWeight: '500',
	},
	headlineLarge: {
		fontSize: 32,
		fontWeight: '400',
		lineHeight: 40,
	},
	headlineMedium: {
		fontSize: 28,
		fontWeight: '400',
		lineHeight: 36,
	},
	headlineSmall: {
		fontSize: 24,
		fontWeight: '400',
		lineHeight: 32,
	},
	titleLarge: {
		fontSize: 22,
		fontWeight: '400',
		lineHeight: 28,
	},
	titleMedium: {
		fontSize: 16,
		fontWeight: '500',
		lineHeight: 24,
	},
	titleSmall: {
		fontSize: 14,
		fontWeight: '500',
		lineHeight: 20,
	},
	bodyLarge: {
		fontSize: 16,
		fontWeight: '400',
		lineHeight: 24,
	},
	bodyMedium: {
		fontSize: 14,
		fontWeight: '400',
		lineHeight: 20,
	},
	bodySmall: {
		fontSize: 12,
		fontWeight: '400',
		lineHeight: 16,
	},
	labelLarge: {
		fontSize: 14,
		fontWeight: '500',
		lineHeight: 20,
	},
	labelMedium: {
		fontSize: 12,
		fontWeight: '500',
		lineHeight: 16,
	},
	labelSmall: {
		fontSize: 11,
		fontWeight: '500',
		lineHeight: 16,
	},
});
